import type {
  ApiError,
  App,
  AppPublishKeyCreated,
  AppPublishKeyListItem,
  Channel,
  ChannelChildren,
  ChannelHistoryEntry,
  ChannelListItem,
  ChannelSubscriber,
  ClientKey,
  ClientKeyCreated,
  ClientKeyListItem,
  CredentialEnvironment,
  CredentialListItem,
  CredentialProvider,
  GenerateCredentialRequest,
  GenerateCredentialResult,
  PutCredentialRequest,
  PutCredentialResult,
  CreateAppPublishKeyRequest,
  CreateAppRequest,
  CreateChannelRequest,
  CreateNamespaceRequest,
  CreateWebhookRequest,
  CreateClientKeyRequest,
  CreateJoinKeyRequest,
  CreateManagementKeyRequest,
  CreateOrgRequest,
  CreatePublishKeyRequest,
  ManagementKey,
  ManagementKeyCreated,
  UpdateManagementKeyRequest,
  InviteListItem,
  InviteMemberRequest,
  InviteResolution,
  JoinKey,
  JoinKeyListItem,
  Listing,
  Me,
  Member,
  MemberInviteResult,
  MemberListItem,
  Org,
  OrgListItem,
  PageResponse,
  Profile,
  PublishKey,
  PublishKeyCreated,
  PublishKeyListItem,
  DeliveryFailureList,
  DeliveryFailureParams,
  NotifySubjectRequest,
  NotifySubjectResult,
  SendChannelMessageRequest,
  SendChannelMessageResult,
  SubjectDetail,
  SubjectChannel,
  SubjectDevice,
  EnrolSubjectResult,
  SubjectEraseResult,
  SubjectListItem,
  SubjectListParams,
  UpdateAppRequest,
  Namespace,
  NamespaceDetail,
  UpdateChannelRequest,
  UpdateNamespaceRequest,
  UpdateWebhookRequest,
  Webhook,
  WebhookSigningKey,
  UpdateClientKeyRequest,
  UpdateJoinKeyRequest,
  UpdateMemberRequest,
  UpdateOrgRequest,
  UpdateProfileRequest,
  UpdatePublishKeyRequest,
} from '@poke-me/shared';

export class ApiClientError extends Error {
  readonly code: number;
  readonly details?: Record<string, unknown>;

  constructor(payload: ApiError) {
    super(payload.message);
    this.name = 'ApiClientError';
    this.code = payload.code;
    if (payload.details !== undefined) {
      this.details = payload.details;
    }
  }
}

export interface ApiClient {
  getMe(): Promise<Me>;
  getProfile(): Promise<Profile>;
  updateProfile(body: UpdateProfileRequest): Promise<Profile>;
  listMyOrgs(): Promise<Listing<OrgListItem>>;
  createOrg(body: CreateOrgRequest): Promise<Org>;
  getOrg(orgRef: string): Promise<Org>;
  updateOrg(orgRef: string, body: UpdateOrgRequest): Promise<Org>;
  deleteOrg(orgRef: string): Promise<void>;
  listMembers(
    orgRef: string,
    params?: { cursor?: string; limit?: number },
  ): Promise<PageResponse<MemberListItem>>;
  inviteMember(orgRef: string, body: InviteMemberRequest): Promise<MemberInviteResult>;
  updateMember(orgRef: string, userRef: string, body: UpdateMemberRequest): Promise<Member>;
  removeMember(orgRef: string, userRef: string): Promise<void>;
  listInvites(orgRef: string): Promise<Listing<InviteListItem>>;
  revokeInvite(orgRef: string, inviteRef: string): Promise<void>;
  resolveInvite(token: string): Promise<InviteResolution>;
  acceptInvite(token: string): Promise<Org>;
  listApps(
    orgRef: string,
    params?: { cursor?: string; limit?: number },
  ): Promise<PageResponse<App>>;
  createApp(orgRef: string, body: CreateAppRequest): Promise<App>;
  getApp(orgRef: string, appRef: string): Promise<App>;
  updateApp(orgRef: string, appRef: string, body: UpdateAppRequest): Promise<App>;
  deleteApp(orgRef: string, appRef: string): Promise<void>;
  listAppPublishKeys(orgRef: string, appRef: string): Promise<Listing<AppPublishKeyListItem>>;
  createAppPublishKey(
    orgRef: string,
    appRef: string,
    body: CreateAppPublishKeyRequest,
  ): Promise<AppPublishKeyCreated>;
  listClientKeys(orgRef: string, appRef: string): Promise<Listing<ClientKeyListItem>>;
  createClientKey(
    orgRef: string,
    appRef: string,
    body: CreateClientKeyRequest,
  ): Promise<ClientKeyCreated>;
  updateClientKey(
    orgRef: string,
    appRef: string,
    keyRef: string,
    body: UpdateClientKeyRequest,
  ): Promise<ClientKey>;
  deleteClientKey(orgRef: string, appRef: string, keyRef: string): Promise<void>;
  listCredentials(orgRef: string, appRef: string): Promise<Listing<CredentialListItem>>;
  putCredential(
    orgRef: string,
    appRef: string,
    provider: CredentialProvider,
    environment: CredentialEnvironment,
    body: PutCredentialRequest,
  ): Promise<PutCredentialResult>;
  generateCredential(
    orgRef: string,
    appRef: string,
    provider: CredentialProvider,
    environment: CredentialEnvironment,
    body: GenerateCredentialRequest,
  ): Promise<GenerateCredentialResult>;
  deleteCredential(
    orgRef: string,
    appRef: string,
    provider: CredentialProvider,
    environment: CredentialEnvironment,
  ): Promise<void>;
  listSubjects(
    orgRef: string,
    appRef: string,
    params?: SubjectListParams,
  ): Promise<PageResponse<SubjectListItem>>;
  getSubject(orgRef: string, appRef: string, subjectRef: string): Promise<SubjectDetail>;
  listSubjectDevices(
    orgRef: string,
    appRef: string,
    subjectRef: string,
    params?: { cursor?: string; limit?: number },
  ): Promise<PageResponse<SubjectDevice>>;
  eraseSubject(orgRef: string, appRef: string, subjectRef: string): Promise<SubjectEraseResult>;
  listSubjectChannels(
    orgRef: string,
    appRef: string,
    subjectRef: string,
  ): Promise<Listing<SubjectChannel>>;
  enrolSubjectChannel(
    orgRef: string,
    appRef: string,
    subjectRef: string,
    channelRef: string,
  ): Promise<EnrolSubjectResult>;
  revokeSubjectChannel(
    orgRef: string,
    appRef: string,
    subjectRef: string,
    channelRef: string,
  ): Promise<{ revoked: boolean }>;
  listDeliveryFailures(
    orgRef: string,
    appRef: string,
    params?: DeliveryFailureParams,
  ): Promise<DeliveryFailureList>;
  sendChannelMessage(
    orgRef: string,
    channelRef: string,
    body: SendChannelMessageRequest,
  ): Promise<SendChannelMessageResult>;
  notifySubject(
    orgRef: string,
    appRef: string,
    body: NotifySubjectRequest,
  ): Promise<NotifySubjectResult>;
  listOrgChannels(
    orgRef: string,
    params?: { cursor?: string; limit?: number },
  ): Promise<PageResponse<ChannelListItem>>;
  getOrgChannel(orgRef: string, channelRef: string): Promise<Channel>;
  /**
   * One level of the namespace tree: the channels directly in `nsRef`, then its
   * child namespaces. `nsRef` is a namespace uuid or slug-path; `''` is the org
   * root, where the channels that live outside any namespace are.
   */
  listChannelChildren(
    orgRef: string,
    nsRef: string,
    params?: { limit?: number; channelCursor?: string; namespaceCursor?: string },
  ): Promise<ChannelChildren>;
  listOrgNamespaces(orgRef: string): Promise<{ items: Namespace[] }>;
  getOrgNamespace(orgRef: string, nsRef: string): Promise<NamespaceDetail>;
  createOrgNamespace(orgRef: string, body: CreateNamespaceRequest): Promise<Namespace>;
  updateOrgNamespace(
    orgRef: string,
    nsRef: string,
    body: UpdateNamespaceRequest,
  ): Promise<Namespace>;
  deleteOrgNamespace(orgRef: string, nsRef: string): Promise<void>;
  listOrgWebhooks(orgRef: string): Promise<{ items: Webhook[] }>;
  createOrgWebhook(orgRef: string, body: CreateWebhookRequest): Promise<Webhook>;
  updateOrgWebhook(orgRef: string, hookId: string, body: UpdateWebhookRequest): Promise<Webhook>;
  deleteOrgWebhook(orgRef: string, hookId: string): Promise<void>;
  /** "I have fixed the handler" — the only way back from a ban. */
  reinstateOrgWebhook(orgRef: string, hookId: string): Promise<Webhook>;
  listOrgWebhookKeys(orgRef: string): Promise<{ items: WebhookSigningKey[] }>;
  rotateOrgWebhookKey(orgRef: string): Promise<WebhookSigningKey>;
  listChannelHistory(
    orgRef: string,
    channelRef: string,
    params?: { cursor?: string; limit?: number },
  ): Promise<PageResponse<ChannelHistoryEntry>>;
  listChannelSubscribers(
    orgRef: string,
    channelRef: string,
    params?: { cursor?: string; limit?: number },
  ): Promise<PageResponse<ChannelSubscriber>>;
  createChannel(orgRef: string, body: CreateChannelRequest): Promise<Channel>;
  updateChannel(
    orgRef: string,
    channelRef: string,
    body: UpdateChannelRequest,
  ): Promise<Channel>;
  archiveChannel(orgRef: string, channelRef: string): Promise<void>;
  listPublishKeys(orgRef: string): Promise<Listing<PublishKeyListItem>>;
  getPublishKey(orgRef: string, keyRef: string): Promise<PublishKey>;
  createPublishKey(orgRef: string, body: CreatePublishKeyRequest): Promise<PublishKeyCreated>;
  updatePublishKey(
    orgRef: string,
    keyRef: string,
    body: UpdatePublishKeyRequest,
  ): Promise<PublishKey>;
  deletePublishKey(orgRef: string, keyRef: string): Promise<void>;
  listManagementKeys(orgRef: string): Promise<PageResponse<ManagementKey>>;
  createManagementKey(
    orgRef: string,
    body: CreateManagementKeyRequest,
  ): Promise<ManagementKeyCreated>;
  updateManagementKey(
    orgRef: string,
    keyRef: string,
    body: UpdateManagementKeyRequest,
  ): Promise<ManagementKey>;
  deleteManagementKey(orgRef: string, keyRef: string): Promise<void>;
  listJoinKeys(orgRef: string, channelRef: string): Promise<Listing<JoinKeyListItem>>;
  createJoinKey(orgRef: string, channelRef: string, body: CreateJoinKeyRequest): Promise<JoinKey>;
  /** A join key id is unique, so these three need no channel ref. */
  getJoinKey(orgRef: string, keyId: string): Promise<JoinKey>;
  updateJoinKey(orgRef: string, keyId: string, body: UpdateJoinKeyRequest): Promise<JoinKey>;
  deleteJoinKey(orgRef: string, keyId: string): Promise<void>;
}

export interface ApiClientOptions {
  baseUrl: string;
  /**
   * Resolves an Auth0 access token. Throws when not authenticated.
   * The thrown error is treated as "no auth header" — the BFF will reject
   * with 401, which the SPA can render as a sign-in prompt.
   */
  getToken: () => Promise<string>;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** `/api/orgs/{org}/apps/{app}/subjects/{subject}` — the base the enrolment
 *  endpoints hang off. */
function subjectUrl(orgRef: string, appRef: string, subjectRef: string): string {
  return `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/subjects/${encodeURIComponent(subjectRef)}`;
}

/**
 * Encode a ref that may be a path — a channel like `apps/web/releases`, or a
 * namespace like `apps/web`.
 *
 * `encodeURIComponent` on the whole thing would turn the separators into `%2F`
 * and the server would look for one channel literally called `apps/web/releases`
 * rather than walking the namespace tree. Each segment is escaped on its own so
 * a slug containing a reserved character is still safe, and the slashes that
 * mean "descend" survive.
 */
function encodeRef(ref: string): string {
  return ref.split('/').map(encodeURIComponent).join('/');
}

export function createApiClient({ baseUrl, getToken }: ApiClientOptions): ApiClient {
  const root = baseUrl.replace(/\/$/, '');

  async function request<T>(
    method: Method,
    path: string,
    options?: {
      query?: Record<string, string | number | undefined>;
      body?: unknown;
    },
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    try {
      headers.Authorization = `Bearer ${await getToken()}`;
    } catch {
      // No token — let the BFF return 401.
    }

    let url = `${root}${path}`;
    if (options?.query) {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(options.query)) {
        if (v !== undefined) qs.set(k, String(v));
      }
      const s = qs.toString();
      if (s) url += `?${s}`;
    }

    const init: RequestInit = { method, headers };
    if (options?.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(options.body);
    }

    const res = await fetch(url, init);
    if (!res.ok) {
      let payload: ApiError;
      try {
        payload = (await res.json()) as ApiError;
      } catch {
        payload = { code: res.status, message: res.statusText };
      }
      throw new ApiClientError(payload);
    }
    if (res.status === 204) {
      return undefined as T;
    }
    const text = await res.text();
    if (text.length === 0) {
      return undefined as T;
    }
    return JSON.parse(text) as T;
  }

  return {
    getMe: () => request<Me>('GET', '/api/me'),
    getProfile: () => request<Profile>('GET', '/api/me/profile'),
    updateProfile: (body) => request<Profile>('PATCH', '/api/me/profile', { body }),
    listMyOrgs: () => request<Listing<OrgListItem>>('GET', '/api/me/orgs'),
    createOrg: (body) => request<Org>('POST', '/api/orgs', { body }),
    getOrg: (orgRef) => request<Org>('GET', `/api/orgs/${encodeURIComponent(orgRef)}`),
    updateOrg: (orgRef, body) =>
      request<Org>('PATCH', `/api/orgs/${encodeURIComponent(orgRef)}`, { body }),
    deleteOrg: (orgRef) =>
      request<void>('DELETE', `/api/orgs/${encodeURIComponent(orgRef)}`),
    listMembers: (orgRef, params) =>
      request<PageResponse<MemberListItem>>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/members`,
        { query: { cursor: params?.cursor, limit: params?.limit } },
      ),
    inviteMember: (orgRef, body) =>
      request<MemberInviteResult>(
        'POST',
        `/api/orgs/${encodeURIComponent(orgRef)}/members`,
        { body },
      ),
    updateMember: (orgRef, userRef, body) =>
      request<Member>(
        'PATCH',
        `/api/orgs/${encodeURIComponent(orgRef)}/members/${encodeURIComponent(userRef)}`,
        { body },
      ),
    removeMember: (orgRef, userRef) =>
      request<void>(
        'DELETE',
        `/api/orgs/${encodeURIComponent(orgRef)}/members/${encodeURIComponent(userRef)}`,
      ),
    listInvites: (orgRef) =>
      request<Listing<InviteListItem>>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/invites`,
      ),
    revokeInvite: (orgRef, inviteRef) =>
      request<void>(
        'DELETE',
        `/api/orgs/${encodeURIComponent(orgRef)}/invites/${encodeURIComponent(inviteRef)}`,
      ),
    resolveInvite: (token) =>
      request<InviteResolution>('GET', `/api/invites/${encodeURIComponent(token)}`),
    acceptInvite: (token) =>
      request<Org>('POST', `/api/invites/${encodeURIComponent(token)}/accept`),
    listApps: (orgRef, params) =>
      request<PageResponse<App>>('GET', `/api/orgs/${encodeURIComponent(orgRef)}/apps`, {
        query: { cursor: params?.cursor, limit: params?.limit },
      }),
    createApp: (orgRef, body) =>
      request<App>('POST', `/api/orgs/${encodeURIComponent(orgRef)}/apps`, { body }),
    getApp: (orgRef, appRef) =>
      request<App>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}`,
      ),
    updateApp: (orgRef, appRef, body) =>
      request<App>(
        'PATCH',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}`,
        { body },
      ),
    deleteApp: (orgRef, appRef) =>
      request<void>(
        'DELETE',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}`,
      ),
    listAppPublishKeys: (orgRef, appRef) =>
      request<Listing<AppPublishKeyListItem>>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/publish-keys`,
      ),
    createAppPublishKey: (orgRef, appRef, body) =>
      request<AppPublishKeyCreated>(
        'POST',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/publish-keys`,
        { body },
      ),
    listClientKeys: (orgRef, appRef) =>
      request<Listing<ClientKeyListItem>>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/client-keys`,
      ),
    createClientKey: (orgRef, appRef, body) =>
      request<ClientKeyCreated>(
        'POST',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/client-keys`,
        { body },
      ),
    updateClientKey: (orgRef, appRef, keyRef, body) =>
      request<ClientKey>(
        'PATCH',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/client-keys/${encodeURIComponent(keyRef)}`,
        { body },
      ),
    deleteClientKey: (orgRef, appRef, keyRef) =>
      request<void>(
        'DELETE',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/client-keys/${encodeURIComponent(keyRef)}`,
      ),
    listCredentials: (orgRef, appRef) =>
      request<Listing<CredentialListItem>>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/credentials`,
      ),
    putCredential: (orgRef, appRef, provider, environment, body) =>
      request<PutCredentialResult>(
        'PUT',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/credentials/${encodeURIComponent(provider)}/${encodeURIComponent(environment)}`,
        { body },
      ),
    generateCredential: (orgRef, appRef, provider, environment, body) =>
      request<GenerateCredentialResult>(
        'POST',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/credentials/${encodeURIComponent(provider)}/${encodeURIComponent(environment)}/generate`,
        { body },
      ),
    deleteCredential: (orgRef, appRef, provider, environment) =>
      request<void>(
        'DELETE',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/credentials/${encodeURIComponent(provider)}/${encodeURIComponent(environment)}`,
      ),
    listSubjects: (orgRef, appRef, params) =>
      request<PageResponse<SubjectListItem>>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/subjects`,
        { query: { q: params?.q, cursor: params?.cursor, limit: params?.limit } },
      ),
    getSubject: (orgRef, appRef, subjectRef) =>
      request<SubjectDetail>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/subjects/${encodeURIComponent(subjectRef)}`,
      ),
    listSubjectDevices: (orgRef, appRef, subjectRef, params) =>
      request<PageResponse<SubjectDevice>>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/subjects/${encodeURIComponent(subjectRef)}/devices`,
        { query: { cursor: params?.cursor, limit: params?.limit } },
      ),
    listSubjectChannels: (orgRef, appRef, subjectRef) =>
      request<Listing<SubjectChannel>>(
        'GET',
        `${subjectUrl(orgRef, appRef, subjectRef)}/channels`,
      ),
    enrolSubjectChannel: (orgRef, appRef, subjectRef, channelRef) =>
      request<EnrolSubjectResult>(
        'PUT',
        `${subjectUrl(orgRef, appRef, subjectRef)}/channels/${encodeRef(channelRef)}`,
      ),
    revokeSubjectChannel: (orgRef, appRef, subjectRef, channelRef) =>
      request<{ revoked: boolean }>(
        'DELETE',
        `${subjectUrl(orgRef, appRef, subjectRef)}/channels/${encodeRef(channelRef)}`,
      ),
    eraseSubject: (orgRef, appRef, subjectRef) =>
      request<SubjectEraseResult>(
        'DELETE',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/subjects/${encodeURIComponent(subjectRef)}`,
      ),
    listDeliveryFailures: (orgRef, appRef, params) =>
      request<DeliveryFailureList>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/delivery-failures`,
        { query: { since: params?.since, limit: params?.limit } },
      ),
    sendChannelMessage: (orgRef, channelRef, body) =>
      request<SendChannelMessageResult>(
        'POST',
        `/api/orgs/${encodeURIComponent(orgRef)}/channels/_messages/${encodeRef(channelRef)}`,
        { body },
      ),
    notifySubject: (orgRef, appRef, body) =>
      request<NotifySubjectResult>(
        'POST',
        `/api/orgs/${encodeURIComponent(orgRef)}/apps/${encodeURIComponent(appRef)}/notify`,
        { body },
      ),
    listOrgChannels: (orgRef, params) =>
      request<PageResponse<ChannelListItem>>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/channels`,
        { query: { cursor: params?.cursor, limit: params?.limit } },
      ),
    getOrgChannel: (orgRef, channelRef) =>
      request<Channel>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/channels/${encodeRef(channelRef)}`,
      ),
    listChannelChildren: (orgRef, nsRef, params) =>
      request<ChannelChildren>(
        'GET',
        // The root is the bare `_children` path, not a trailing slash.
        `/api/orgs/${encodeURIComponent(orgRef)}/channels/_children${
          nsRef ? `/${encodeRef(nsRef)}` : ''
        }`,
        {
          query: {
            limit: params?.limit,
            channel_cursor: params?.channelCursor,
            namespace_cursor: params?.namespaceCursor,
          },
        },
      ),
    listOrgNamespaces: (orgRef) =>
      request<{ items: Namespace[] }>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/namespaces`,
      ),
    getOrgNamespace: (orgRef, nsRef) =>
      request<NamespaceDetail>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/namespaces/${encodeRef(nsRef)}`,
      ),
    createOrgNamespace: (orgRef, body) =>
      request<Namespace>('POST', `/api/orgs/${encodeURIComponent(orgRef)}/namespaces`, { body }),
    updateOrgNamespace: (orgRef, nsRef, body) =>
      request<Namespace>(
        'PATCH',
        `/api/orgs/${encodeURIComponent(orgRef)}/namespaces/${encodeRef(nsRef)}`,
        { body },
      ),
    deleteOrgNamespace: (orgRef, nsRef) =>
      request<void>(
        'DELETE',
        `/api/orgs/${encodeURIComponent(orgRef)}/namespaces/${encodeRef(nsRef)}`,
      ),
    listOrgWebhooks: (orgRef) =>
      request<{ items: Webhook[] }>('GET', `/api/orgs/${encodeURIComponent(orgRef)}/webhooks`),
    createOrgWebhook: (orgRef, body) =>
      request<Webhook>('POST', `/api/orgs/${encodeURIComponent(orgRef)}/webhooks`, { body }),
    updateOrgWebhook: (orgRef, hookId, body) =>
      request<Webhook>(
        'PATCH',
        `/api/orgs/${encodeURIComponent(orgRef)}/webhooks/${encodeURIComponent(hookId)}`,
        { body },
      ),
    deleteOrgWebhook: (orgRef, hookId) =>
      request<void>(
        'DELETE',
        `/api/orgs/${encodeURIComponent(orgRef)}/webhooks/${encodeURIComponent(hookId)}`,
      ),
    reinstateOrgWebhook: (orgRef, hookId) =>
      request<Webhook>(
        'POST',
        `/api/orgs/${encodeURIComponent(orgRef)}/webhooks/${encodeURIComponent(hookId)}/reinstate`,
        { body: {} },
      ),
    listOrgWebhookKeys: (orgRef) =>
      request<{ items: WebhookSigningKey[] }>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/webhook-keys`,
      ),
    rotateOrgWebhookKey: (orgRef) =>
      request<WebhookSigningKey>(
        'POST',
        `/api/orgs/${encodeURIComponent(orgRef)}/webhook-keys`,
        { body: {} },
      ),
    listChannelHistory: (orgRef, channelRef, params) =>
      request<PageResponse<ChannelHistoryEntry>>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/channels/_history/${encodeRef(channelRef)}`,
        { query: { cursor: params?.cursor, limit: params?.limit } },
      ),
    listChannelSubscribers: (orgRef, channelRef, params) =>
      request<PageResponse<ChannelSubscriber>>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/channels/_subscribers/${encodeRef(channelRef)}`,
        { query: { cursor: params?.cursor, limit: params?.limit } },
      ),
    createChannel: (orgRef, body) =>
      request<Channel>(
        'POST',
        `/api/orgs/${encodeURIComponent(orgRef)}/channels`,
        { body },
      ),
    updateChannel: (orgRef, channelRef, body) =>
      request<Channel>(
        'PATCH',
        `/api/orgs/${encodeURIComponent(orgRef)}/channels/${encodeRef(channelRef)}`,
        { body },
      ),
    archiveChannel: (orgRef, channelRef) =>
      request<void>(
        'DELETE',
        `/api/orgs/${encodeURIComponent(orgRef)}/channels/${encodeRef(channelRef)}`,
      ),
    listPublishKeys: (orgRef) =>
      request<Listing<PublishKeyListItem>>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/publish-keys`,
      ),
    getPublishKey: (orgRef, keyRef) =>
      request<PublishKey>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/publish-keys/${encodeURIComponent(keyRef)}`,
      ),
    createPublishKey: (orgRef, body) =>
      request<PublishKeyCreated>(
        'POST',
        `/api/orgs/${encodeURIComponent(orgRef)}/publish-keys`,
        { body },
      ),
    updatePublishKey: (orgRef, keyRef, body) =>
      request<PublishKey>(
        'PATCH',
        `/api/orgs/${encodeURIComponent(orgRef)}/publish-keys/${encodeURIComponent(keyRef)}`,
        { body },
      ),
    deletePublishKey: (orgRef, keyRef) =>
      request<void>(
        'DELETE',
        `/api/orgs/${encodeURIComponent(orgRef)}/publish-keys/${encodeURIComponent(keyRef)}`,
      ),
    listManagementKeys: (orgRef) =>
      request<PageResponse<ManagementKey>>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/management-keys`,
      ),
    createManagementKey: (orgRef, body) =>
      request<ManagementKeyCreated>(
        'POST',
        `/api/orgs/${encodeURIComponent(orgRef)}/management-keys`,
        { body },
      ),
    updateManagementKey: (orgRef, keyRef, body) =>
      request<ManagementKey>(
        'PATCH',
        `/api/orgs/${encodeURIComponent(orgRef)}/management-keys/${encodeURIComponent(keyRef)}`,
        { body },
      ),
    deleteManagementKey: (orgRef, keyRef) =>
      request<void>(
        'DELETE',
        `/api/orgs/${encodeURIComponent(orgRef)}/management-keys/${encodeURIComponent(keyRef)}`,
      ),
    listJoinKeys: (orgRef, channelRef) =>
      request<Listing<JoinKeyListItem>>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/channels/_join-keys/${encodeRef(channelRef)}`,
      ),
    getJoinKey: (orgRef, keyId) =>
      request<JoinKey>(
        'GET',
        `/api/orgs/${encodeURIComponent(orgRef)}/join-keys/${encodeURIComponent(keyId)}`,
      ),
    createJoinKey: (orgRef, channelRef, body) =>
      request<JoinKey>(
        'POST',
        `/api/orgs/${encodeURIComponent(orgRef)}/channels/_join-keys/${encodeRef(channelRef)}`,
        { body },
      ),
    updateJoinKey: (orgRef, keyId, body) =>
      request<JoinKey>(
        'PATCH',
        `/api/orgs/${encodeURIComponent(orgRef)}/join-keys/${encodeURIComponent(keyId)}`,
        { body },
      ),
    deleteJoinKey: (orgRef, keyId) =>
      request<void>(
        'DELETE',
        `/api/orgs/${encodeURIComponent(orgRef)}/join-keys/${encodeURIComponent(keyId)}`,
      ),
  };
}
